import { Fragment, useRef } from 'react';
import { ArrowRight } from 'lucide-react';

import { EscrowPartyTypeEnum } from 'src/graphql/schema';
import { IconBeneficiaryH, IconDepositorH } from 'src/components/Svg/Icons';
import { OrganizationLogo } from 'src/components/Organization';
import { cn } from 'src/lib/utils';
import { useIsPairWrapped } from 'src/hooks/use-is-pair-wrapped';

import { useEscrowDashboardCard } from '../model/context';

const PartyLogo = ({
  logo,
  name,
  isNew,
  partyType,
}: {
  logo: string;
  name: string;
  isNew: boolean;
  partyType: EscrowPartyTypeEnum;
}) => {
  const PartyIcon = partyType === EscrowPartyTypeEnum.Depositor ? IconDepositorH : IconBeneficiaryH;

  if (isNew)
    return (
      <div className="size-6 border border-muted bg-muted rounded-sm flex items-center justify-center shrink-0">
        <PartyIcon className="size-3.5 text-gray-400" />
      </div>
    );

  return <OrganizationLogo logo={logo} name={name} size="sm" />;
};

const Parties = () => {
  const { viewModel } = useEscrowDashboardCard();
  const { parties } = viewModel;

  const containerRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef<HTMLDivElement>(null);
  const secondRef = useRef<HTMLDivElement>(null);
  const isWrapped = useIsPairWrapped({
    containerRef,
    firstItemRef: firstRef,
    secondItemRef: secondRef,
  });

  return (
    <div
      ref={containerRef}
      className={cn('flex flex-wrap items-center gap-2 mb-6', isWrapped && 'flex-col items-start')}
    >
      {parties.map(({ id, logo, name, isNew, partyType }, index) => (
        <Fragment key={id}>
          <div ref={index === 0 ? firstRef : index === 1 ? secondRef : undefined} className="flex items-start gap-1">
            <PartyLogo logo={logo} name={name} isNew={isNew} partyType={partyType} />
            <div className={cn('text-base font-bold text-foreground', isNew && 'text-gray-400 font-medium')}>
              {name}
            </div>
          </div>
          {index !== parties.length - 1 && <ArrowRight className="size-5 text-foreground shrink-0" />}
        </Fragment>
      ))}
    </div>
  );
};

export { Parties };
